import { Component, computed, input } from '@angular/core';

import { InfoscreenFooterComponent } from '../../infoscreen-dumb/infoscreen-footer/infoscreen-footer.component';
import { InfoscreenListField, InfoscreenPeriod } from '../infoscreen-module.types';
import { lockerRoomText } from '../../school-calendar/locker-room-text';

/** Half-hour slots on the timeline: one before now, then forward. */
const SLOTS_BEFORE = 2;
const SLOTS_TOTAL = 16;
const SLOT_MS = 30 * 60 * 1000;
const UPCOMING = 3;

/** The words of the two one-room screens: a meeting room's, and a changing room's. */
const TEXTS = {
  meeting: { next: 'Næste møde', none: 'Ingen flere møder i dag', now: 'Igangværende møde', free: 'Lokalet er' },
  locker: { next: 'Næste hold', none: 'Ingen flere hold i dag', now: 'I omklædningsrummet nu', free: 'Omklædningsrummet er' },
};

/**
 * One meeting room or changing room, for a small screen by its door. The left side names the room,
 * lists the next meetings (or teams, by the changing room's own times) and shows the time; the right
 * side says what is going on now — the current one, or "Ledigt" and until when. A half-hour
 * timeline runs along the bottom.
 *
 * Sizes follow the stage (`--screen-h`), so it reads the same on a 7" tablet and a 4K screen.
 */
@Component({
  selector: 'app-room-view',
  imports: [InfoscreenFooterComponent],
  template: `
    <div class="room">
      <aside class="side">
        <h1 class="name">{{ roomName() }}</h1>

        <div class="next">
          <div class="label">{{ texts().next }}</div>
          @for (p of upcoming(); track p.id) {
            <div class="item">
              <span class="marker"></span>
              <div>
                <div class="time">{{ hhmm(p.start) }} - {{ hhmm(p.end) }}</div>
                <div class="who">{{ who(p) }}</div>
              </div>
            </div>
          } @empty {
            <div class="none">{{ texts().none }}</div>
          }
        </div>

        <div class="clock">{{ hhmm(now()) }}</div>
      </aside>

      <main class="status" [class.free]="!current()">
        <app-infoscreen-footer class="logo" />

        @if (current(); as c) {
          <div class="label"><span class="marker"></span>{{ texts().now }}</div>
          <div class="big">{{ hhmm(c.start) }} - {{ hhmm(c.end) }}</div>
          <div class="who">{{ who(c) }}</div>
        } @else {
          <div class="label"><span class="marker free"></span>{{ texts().free }}</div>
          <div class="big">Ledigt</div>
          <div class="who">{{ freeUntil() }}</div>
        }
      </main>

      <footer class="timeline">
        @for (slot of slots(); track slot.at) {
          <div class="slot" [class.busy]="slot.busy" [class.current]="slot.current" [class.now]="slot.now">{{ slot.label }}</div>
        }
      </footer>
    </div>
  `,
  styles: `
    :host {
      display: block;
      height: var(--screen-h, 100vh);
      --u: calc(var(--screen-h, 100vh) / 100); /* 1 % of the height */
      --orange: #f2994a;
    }
    .room {
      height: 100%;
      display: grid;
      grid-template-columns: minmax(0, 38fr) minmax(0, 62fr);
      grid-template-rows: minmax(0, 1fr) calc(var(--u) * 11);
      background: transparent; /* the theme's background (and any decorations) is the screen host's */
      color: var(--text-color, #fff);
      font-family: inherit;
    }
    .side {
      background: #fbf9f4;
      color: #1d2a44;
      padding: calc(var(--u) * 5) calc(var(--u) * 4);
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    .name {
      margin: 0;
      font-size: calc(var(--u) * 6.5);
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      line-height: 1.05;
      padding-bottom: calc(var(--u) * 1);
      border-bottom: calc(var(--u) * 0.6) solid var(--orange);
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .label {
      display: flex;
      align-items: center;
      gap: calc(var(--u) * 1.2);
      font-size: calc(var(--u) * 3);
      opacity: 0.85;
    }
    .next {
      margin-top: calc(var(--u) * 3);
      display: flex;
      flex-direction: column;
      gap: calc(var(--u) * 2);
      min-height: 0;
      overflow: hidden;
    }
    .item {
      display: flex;
      gap: calc(var(--u) * 1.5);
      align-items: flex-start;
      .time { font-size: calc(var(--u) * 3); font-weight: 700; }
      .who { font-size: calc(var(--u) * 2.6); opacity: 0.8; }
    }
    .none { font-size: calc(var(--u) * 2.8); opacity: 0.7; }
    .marker {
      flex-shrink: 0;
      width: calc(var(--u) * 1.6);
      height: calc(var(--u) * 1.6);
      margin-top: calc(var(--u) * 0.8);
      background: var(--orange);
      &.free { background: #6fcf97; }
    }
    .label .marker { margin-top: 0; }
    .clock {
      margin-top: auto;
      font-size: calc(var(--u) * 15);
      font-weight: 800;
      line-height: 1;
      color: #1d2a44;
    }
    .status {
      position: relative;
      padding: calc(var(--u) * 16) calc(var(--u) * 6) calc(var(--u) * 5);
      display: flex;
      flex-direction: column;
      gap: calc(var(--u) * 2);
      min-height: 0;
      .big { font-size: calc(var(--u) * 11); font-weight: 800; line-height: 1; }
      .who { font-size: calc(var(--u) * 4.5); }
    }
    /* The a·book logo from the old footer, top right, without its line of text. */
    .logo {
      position: absolute;
      top: calc(var(--u) * 5);
      right: calc(var(--u) * 5);
      height: calc(var(--u) * 7);
      ::ng-deep footer { height: 100%; }
      ::ng-deep svg { height: 100%; width: auto; }
      ::ng-deep .location-name { display: none; }
    }
    .timeline {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: repeat(16, minmax(0, 1fr));
      align-items: stretch;
      background: rgba(0, 0, 0, 0.15);
      border-top: calc(var(--u) * 0.4) solid rgba(255, 255, 255, 0.8);
    }
    .slot {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: calc(var(--u) * 2.4);
      color: var(--text-color, #fff);
      &.busy { background: rgba(242, 153, 74, 0.35); }
      &.current { background: var(--orange); color: #fff; font-weight: 700; }
      /* The half hour we are in: a bar along its top edge. */
      &.now { box-shadow: inset 0 calc(var(--u) * 0.8) 0 #ffffff; font-weight: 700; }
    }
  `,
})
export class RoomViewComponent {
  readonly periods = input.required<InfoscreenPeriod[]>();
  readonly roomName = input.required<string>();
  readonly now = input.required<Date>();
  /** What is written at each meeting, in order. */
  readonly fields = input<InfoscreenListField[]>(['Note']);
  /** A meeting room, or a changing room: the same screen in other words. */
  readonly kind = input<'meeting' | 'locker'>('meeting');

  readonly texts = computed(() => TEXTS[this.kind()]);

  private readonly sorted = computed(() =>
    [...this.periods()].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),
  );

  /** The meeting going on now, if any. */
  readonly current = computed(() => {
    const now = this.now().getTime();
    return this.sorted().find((p) => new Date(p.start).getTime() <= now && new Date(p.end).getTime() > now) ?? null;
  });

  readonly upcoming = computed(() => {
    const now = this.now().getTime();
    return this.sorted()
      .filter((p) => new Date(p.start).getTime() > now)
      .slice(0, UPCOMING);
  });

  readonly freeUntil = computed(() => {
    const next = this.upcoming()[0];
    return next ? `indtil ${this.hhmm(next.start)}` : 'resten af dagen';
  });

  /** Half-hour slots around now; a slot is busy when any meeting overlaps it. */
  readonly slots = computed(() => {
    const now = this.now();
    const first = new Date(now);
    first.setMinutes(now.getMinutes() < 30 ? 0 : 30, 0, 0);
    const start = first.getTime() - SLOTS_BEFORE * SLOT_MS;
    const current = this.current();

    return Array.from({ length: SLOTS_TOTAL }, (_, i) => {
      const at = start + i * SLOT_MS;
      const end = at + SLOT_MS;
      const overlaps = (p: InfoscreenPeriod) => new Date(p.start).getTime() < end && new Date(p.end).getTime() > at;
      return {
        at,
        label: this.hhmm(new Date(at)),
        busy: this.sorted().some(overlaps),
        current: !!current && overlaps(current),
        now: now.getTime() >= at && now.getTime() < end,
      };
    });
  });

  /** "13.30" — the Danish way on a sign. */
  hhmm(value: string | Date): string {
    const d = typeof value === 'string' ? new Date(value) : value;
    return `${String(d.getHours()).padStart(2, '0')}.${String(d.getMinutes()).padStart(2, '0')}`;
  }

  /**
   * What is written at a meeting (or team): the chosen fields that have a value, with " · " between
   * them. With none of them, the screen text, else the activity — so none is ever blank.
   */
  who(p: InfoscreenPeriod): string {
    const text = this.fields()
      .map((field) => this.fieldText(field, p))
      .filter((value) => !!value)
      .join(' · ');
    return text || p.title || p.activity || '';
  }

  private fieldText(field: InfoscreenListField, p: InfoscreenPeriod): string {
    switch (field) {
      case 'Time':
        return `${this.hhmm(p.start)} - ${this.hhmm(p.end)}`;
      case 'Activity':
        return p.activity ?? '';
      case 'Level':
        return p.skillLevel ?? '';
      case 'Facility':
        return p.resource ?? '';
      case 'LockerRooms':
        // On a changing room's screen the booking's text is the team (its time and name are the
        // screen's own); elsewhere the room, its text and its time.
        return (p.lockerRooms ?? [])
          .map((room) => (this.kind() === 'locker' ? room.displayText : lockerRoomText(room, '.')))
          .join(', ');
      case 'Note':
        return p.title ?? '';
    }
  }
}
